/**
 * Turning a packet record into what a row shows.
 *
 * Pure, and that is the whole point for one case in particular. A message we
 * could not parse arrives with `msh: null`, a `parseError` and `ackCode: 'AR'`,
 * and the reference UI map calls it "the case the screen exists for". But it
 * CANNOT be produced through the HTTP API — both `mimic/inbound` and `send`
 * parse the MSH and reject with 400 before anything is dialled, so only a raw
 * TCP peer on a listener port can make one. Combined with this repo having no
 * jsdom and no .tsx test runner, pushing the branch into a function here is the
 * only way it gets covered at all. Components must read these fields through
 * these helpers so the null branch cannot be reintroduced.
 */
import type { Hl7MessageSummary } from './types'

export type AckTone = 'green' | 'amber' | 'red' | 'grey'

/** `ADT^A08`, or a marker when the message could not be parsed. */
export function messageTypeLabel(message: Pick<Hl7MessageSummary, 'msh'>): string {
  if (!message.msh) return 'unparsed'
  const { type, trigger } = message.msh
  return trigger ? `${type}^${trigger}` : type
}

/** True when the row should carry a visible "we rejected this" marker. */
export function isUnparsed(message: Pick<Hl7MessageSummary, 'msh'>): boolean {
  return message.msh === null
}

/** `EPIC→T21`, or an em-dash placeholder when there is no MSH to read it from. */
export function messageRoute(message: Pick<Hl7MessageSummary, 'msh'>): string {
  if (!message.msh) return '—'
  const { sendingApp, receivingApp } = message.msh
  if (!sendingApp && !receivingApp) return '—'
  return `${sendingApp || '?'}→${receivingApp || '?'}`
}

/** MSH-10, or a placeholder. Never undefined, so a row cannot render "undefined". */
export function messageControlId(message: Pick<Hl7MessageSummary, 'msh'>): string {
  return message.msh?.controlId || '—'
}

export function ackTone(message: Pick<Hl7MessageSummary, 'ackCode' | 'parseError'>): AckTone {
  if (message.parseError) return 'red'
  switch (message.ackCode) {
    case 'AA':
      return 'green'
    case 'AE':
      return 'amber'
    case 'AR':
      return 'red'
    default:
      // Outbound sends that never got an acknowledgment at all. Defined rather
      // than undefined, so nothing renders a blank swatch.
      return 'grey'
  }
}

export function ackLabel(message: Pick<Hl7MessageSummary, 'ackCode'>): string {
  return message.ackCode ?? 'no ACK'
}

/**
 * Wall clock with milliseconds, not "3s ago".
 *
 * A packet log is forensic evidence: an operator correlating against MedStar's
 * own send log needs the actual time, and in a burst every row would read "3s
 * ago" and carry no information. Milliseconds matter because two messages can
 * share a second — the reason the tail cursor is `seq` and not a timestamp.
 *
 * The date is part of that evidence too: a log left open across midnight, or
 * reviewed the next morning, is ambiguous without it.
 *
 * Read through the local getters rather than slicing the ISO string. The whole
 * value is localized, so the DATE has to be the local one — `2026-09-01T02:00Z`
 * is the 31st of August for a viewer at UTC-4, and `iso.slice(0, 10)` would
 * print a day that never happened for them.
 */
export function packetTimestamp(iso: string | null | undefined): string {
  // Not folded into the NaN check below: `new Date(null)` is the 1970 epoch,
  // not an invalid date, so a null timestamp would render as 1970-01-01.
  if (!iso) return '—'
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return '—'
  const yyyy = date.getFullYear()
  const mon = String(date.getMonth() + 1).padStart(2, '0')
  const dd = String(date.getDate()).padStart(2, '0')
  const hh = String(date.getHours()).padStart(2, '0')
  const mm = String(date.getMinutes()).padStart(2, '0')
  const ss = String(date.getSeconds()).padStart(2, '0')
  const ms = String(date.getMilliseconds()).padStart(3, '0')
  return `${yyyy}-${mon}-${dd} ${hh}:${mm}:${ss}.${ms}`
}

export function formatBytes(bytes: number): string {
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} kB`
}

/**
 * Why an outbound send failed, in the row.
 *
 * A control-id mismatch is worth naming explicitly: the harness treats a peer
 * that answers AA for some OTHER message as a failure, and a screen that just
 * showed "AA" would hide the one failure worth catching.
 */
export function sendOutcomeLabel(
  message: Pick<Hl7MessageSummary, 'ok' | 'reason' | 'ackCode' | 'acknowledgedControlId' | 'msh'>,
): string | null {
  if (message.ok !== false) {
    const sent = message.msh?.controlId
    const echoed = message.acknowledgedControlId
    if (sent && echoed && sent !== echoed) return `control id mismatch: peer echoed ${echoed}`
    return null
  }
  if (message.reason) return message.reason
  return message.ackCode ? `peer answered ${message.ackCode}` : 'send failed'
}
